import type { Analytics } from '@segment/analytics-next'
import { AnalyticsBrowser } from '@segment/analytics-next'
import * as Sentry from '@sentry/nextjs'
import { isPrd } from '@shared/util/env'

const SEGMENT_WRITE_KEY = process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY

let analyticsInstance: Analytics | null = null
let initializationPromise: Promise<Analytics | null> | null = null

/**
 * Initialize Segment analytics with proxy configuration to bypass ad blockers.
 * Uses Next.js rewrites to proxy requests through step.com/a/seg/*
 */
export const initSegment = async (): Promise<Analytics | null> => {
    // Return existing instance if already initialized
    if (analyticsInstance) {
        return analyticsInstance
    }

    // Return existing promise if initialization is in progress
    if (initializationPromise) {
        return initializationPromise
    }

    if (!SEGMENT_WRITE_KEY) {
        if (!isPrd()) {
            console.warn(
                'Segment write key is missing! Check NEXT_PUBLIC_SEGMENT_WRITE_KEY in your .env file.',
            )
        }
        Sentry.captureMessage('Segment write key is missing', {
            level: 'warning',
            tags: {
                errorType: 'missing_write_key',
                analytics: 'segment',
            },
        })
        return null
    }

    initializationPromise = (async () => {
        try {
            // Use current hostname for API host to support local dev and production
            const apiHost = `${window.location.host}/a/seg/v1`

            const [analytics] = await AnalyticsBrowser.load(
                {
                    writeKey: SEGMENT_WRITE_KEY,
                    // Proxy CDN through Next.js rewrite to bypass ad blockers
                    cdnURL: '/a/seg-cdn',
                },
                {
                    integrations: {
                        'Segment.io': {
                            // Proxy API through Next.js rewrite to bypass ad blockers
                            apiHost,
                            protocol: window.location.protocol.replace(
                                ':',
                                '',
                            ) as 'http' | 'https',
                        },
                    },
                },
            )

            analyticsInstance = analytics
            return analytics
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    errorType: 'initialization_failed',
                    analytics: 'segment',
                },
            })
            if (!isPrd()) {
                console.error('Failed to initialize Segment:', error)
            }
            initializationPromise = null
            return null
        }
    })()

    return initializationPromise
}

/**
 * Get the Segment analytics instance.
 * Returns null if not yet initialized or if initialization failed.
 */
export const getSegmentAnalytics = (): Analytics | null => {
    return analyticsInstance
}

/**
 * Track an event in Segment.
 * Gracefully handles cases where Segment is not initialized.
 */
export const segmentTrack = (
    eventName: string,
    properties?: Record<string, unknown>,
    options?: {
        context?: {
            ip?: string
            traits?: { email?: string; phone?: string }
            referrer?: { id: string; type: string }
        }
    },
): void => {
    if (!analyticsInstance) {
        if (!isPrd()) {
            console.warn(
                'Segment not initialized, cannot track event:',
                eventName,
            )
        }
        return
    }

    try {
        analyticsInstance.track(eventName, properties, options)
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'track_event_failed',
                analytics: 'segment',
            },
            extra: { eventName },
        })
        if (!isPrd()) {
            console.error('Failed to track Segment event:', eventName, error)
        }
    }
}

/**
 * Identify a user in Segment.
 * Gracefully handles cases where Segment is not initialized.
 * @param userId - User ID (optional, pass undefined to identify with traits only)
 * @param traits - User traits to set
 */
export const segmentIdentify = (
    userId?: string,
    traits?: Record<string, unknown>,
): void => {
    if (!analyticsInstance) {
        if (!isPrd()) {
            console.warn('Segment not initialized, cannot identify user')
        }
        return
    }

    try {
        if (userId) {
            analyticsInstance.identify(userId, traits)
        } else if (traits) {
            analyticsInstance.identify(traits)
        }
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'identify_user_failed',
                analytics: 'segment',
            },
        })
        if (!isPrd()) {
            console.error('Failed to identify Segment user:', error)
        }
    }
}
